import { AsyncThunk, createAsyncThunk } from '@reduxjs/toolkit';
import { HttpError, HttpMethods, SERVICES } from '../../../services/api';
import { basicApiAsyncThunkCreator } from '../../../services/apiAsyncThunkCreator';
import { ThunkAPI } from '../../rootReducer';
import { ICheckEmailResponse, ICreateUsersInBulkResponse } from './index';
import { showSuccessMessage } from '../../notifications';
import { ListParams } from '../../utils';
import { FilterOperators, Ifilter } from '../../../components/common/filters/interfaces';
import { fetchAccountsList } from '../../accounts/list.thunks';
import { ActiveInactiveFilterState } from '../../../components/common/activeInactiveToggle';
import { actions as userListActions } from '../list';

export interface IUserForBulkCreation {
  email: string;
  firstName: string;
  lastName: string;
  isOwner?: boolean;
}

export interface IUserBulkCreationPayload {
  accountId: string;
  users: IUserForBulkCreation[];
}

export interface ICheckEmailPayload {
  email: string;
}

export const checkEmail: AsyncThunk<ICheckEmailResponse, ICheckEmailPayload, ThunkAPI> = createAsyncThunk(
  'users/checkEmail',
  async (data: ICheckEmailPayload, thunk) => {
    try {
      return await basicApiAsyncThunkCreator<ICheckEmailResponse, ICheckEmailPayload>({
        endpoint: 'users/checkEmail',
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);
    } catch (err) {
      let errorMessage = JSON.stringify(err);
      if (err instanceof HttpError) {
        errorMessage = err.message;
      }
      return thunk.rejectWithValue(errorMessage);
    }
  }
);

export const createUsersInBulk: AsyncThunk<void, IUserBulkCreationPayload, ThunkAPI> = createAsyncThunk(
  'users/bulk',
  async (data: IUserBulkCreationPayload, thunk) => {
    try {
      const response = await basicApiAsyncThunkCreator<void, IUserBulkCreationPayload>({
        endpoint: 'users/bulk',
        microservice: SERVICES.USERS_SERVICE,
        authenticated: true,
        method: HttpMethods.Post,
        contentType: 'application/json'
      })(data, thunk);
      const request: ListParams<Ifilter[]> = {
        active: ActiveInactiveFilterState.Active,
        filters: [{
          field: 'accountId',
          operands: [data.accountId],
          operator: FilterOperators.isAnyOf,
          validateInputs: false
        }],
        pagination: { offset: 0, limit: 25 }
      };
      thunk.dispatch(userListActions.fetchUsersList(request));
      const fetchAccountsRequest: ListParams<Ifilter[]> = {
        filters: [
          {
            field: '_id',
            operands: [data.accountId],
            operator: FilterOperators.isAnyOf,
            validateInputs: false
          }
        ]
      };
      thunk.dispatch(fetchAccountsList(fetchAccountsRequest));
      thunk.dispatch(showSuccessMessage(`Email invitations have been sent to ${data.users.length} new users`));
      return response;
    } catch (err) {
      if (isUsersInBulkResponseError(err)) {
        const errorsData: ICreateUsersInBulkResponse = {
          errors: err.errors,
          message: err.message
        };
        return thunk.rejectWithValue(errorsData);
      } else if (typeof err === 'string') {
        return thunk.rejectWithValue({ errors: {}, message: err });
      }
      return thunk.rejectWithValue({ errors: {}, message: 'Unknown error' });
    }
  }
);

const isUsersInBulkResponseError = (error: unknown): error is ICreateUsersInBulkResponse => {
  return typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    'errors' in error &&
    typeof error.errors === 'object';
};
